"use client";

import { useCallback, useEffect, useRef, useState } from "react";

type Format = "txt" | "srt" | "json";

interface JobStatus {
  id: string;
  status: "running" | "done" | "error";
  total: number;
  done: number;
  failed: number;
  error?: string;
}

export function BulkClient() {
  const [authState, setAuthState] = useState<"loading" | "anonymous" | "no_plan" | "ready">("loading");
  const [usage, setUsage] = useState<{ used: number; limit: number } | null>(null);
  const [url, setUrl] = useState("");
  const [format, setFormat] = useState<Format>("txt");
  const [job, setJob] = useState<JobStatus | null>(null);
  const [starting, setStarting] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Probe auth/plan state via the usage endpoint
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("/api/bulk/status");
        if (res.status === 401) {
          setAuthState("anonymous");
          return;
        }
        const data = await res.json();
        if (data.usage) {
          setUsage(data.usage);
          setAuthState("ready");
        } else {
          setAuthState("no_plan");
        }
      } catch {
        setAuthState("anonymous");
      }
    })();
  }, []);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  const pollJob = useCallback((jobId: string) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`/api/bulk/status?id=${jobId}`);
        const data = await res.json();
        if (res.ok) {
          setJob(data);
          if (data.status !== "running") stopPolling();
        }
      } catch {
        // transient poll failure — keep trying
      }
    }, 3000);
  }, [stopPolling]);

  const startExport = async () => {
    if (!url.trim()) return;
    setStarting(true);
    setError(null);
    setJob(null);
    try {
      const res = await fetch("/api/bulk/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: url.trim(), format }),
      });
      const data = await res.json();
      if (res.ok) {
        setJob({ id: data.jobId, status: "running", total: data.total, done: 0, failed: 0 });
        pollJob(data.jobId);
      } else if (data.upgrade_required) {
        setAuthState("no_plan");
      } else {
        setError(data.error || "Failed to start export");
      }
    } catch {
      setError("Failed to start export");
    } finally {
      setStarting(false);
    }
  };

  const startCheckout = async () => {
    setCheckoutLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bulkPlan: true }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error || "Failed to start checkout");
      }
    } catch {
      setError("Failed to start checkout");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const progressPct = job && job.total > 0
    ? Math.round(((job.done + job.failed) / job.total) * 100)
    : 0;

  return (
    <div>
      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Export form (subscribed) */}
      {authState === "ready" && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-12">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">New export</h2>
            {usage && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {usage.used.toLocaleString()} / {usage.limit.toLocaleString()} videos this month
              </span>
            )}
          </div>

          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.youtube.com/@channel or playlist URL"
            aria-label="Channel or playlist URL"
            className="w-full px-3 py-2.5 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 text-gray-900 dark:text-white placeholder:text-gray-400 mb-3"
          />

          <div className="flex items-center gap-3 mb-4">
            <span className="text-sm text-gray-500 dark:text-gray-400">Format:</span>
            {(["txt", "srt", "json"] as Format[]).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFormat(f)}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors cursor-pointer ${
                  format === f
                    ? "bg-teal-600 text-white"
                    : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                }`}
              >
                {f.toUpperCase()}
              </button>
            ))}
          </div>

          <button
            onClick={startExport}
            disabled={starting || !url.trim() || job?.status === "running"}
            className="px-5 py-2.5 text-sm font-medium bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded-lg transition-colors cursor-pointer"
          >
            {starting ? "Resolving videos..." : "Start export"}
          </button>

          {/* Progress */}
          {job && (
            <div className="mt-6">
              <div className="flex justify-between text-sm text-gray-600 dark:text-gray-300 mb-2">
                <span>
                  {job.status === "running" && `Extracting: ${job.done + job.failed} / ${job.total}`}
                  {job.status === "done" && `Done — ${job.done} transcripts (${job.failed} without captions)`}
                  {job.status === "error" && `Failed: ${job.error || "unknown error"}`}
                </span>
                <span>{progressPct}%</span>
              </div>
              <div className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${job.status === "error" ? "bg-red-500" : "bg-teal-500"}`}
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              {job.status === "done" && (
                <a
                  href={`/api/bulk/download?id=${job.id}`}
                  className="inline-block mt-4 px-5 py-2.5 text-sm font-medium bg-gray-900 dark:bg-white hover:bg-gray-700 dark:hover:bg-gray-200 text-white dark:text-gray-900 rounded-lg transition-colors"
                >
                  Download zip
                </a>
              )}
            </div>
          )}
        </div>
      )}

      {/* Pricing (not subscribed) */}
      {(authState === "anonymous" || authState === "no_plan") && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-teal-500 ring-1 ring-teal-500 p-6 mb-12 max-w-md">
          <div className="flex items-baseline justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Creator</h3>
            <div>
              <span className="text-3xl font-bold text-gray-900 dark:text-white">$12.99</span>
              <span className="text-sm text-gray-500 dark:text-gray-400">/mo</span>
            </div>
          </div>
          <ul className="space-y-2 mb-6">
            {[
              "Entire channels & playlists in one click",
              "1,000 videos / month, 200 per job",
              "TXT, SRT, or JSON + CSV manifest",
              "Includes unlimited single-video Pro features",
            ].map((f) => (
              <li key={f} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                <svg className="w-4 h-4 text-teal-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                {f}
              </li>
            ))}
          </ul>
          <button
            onClick={startCheckout}
            disabled={checkoutLoading}
            className="w-full px-4 py-2.5 text-sm font-medium bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded-lg transition-colors cursor-pointer"
          >
            {checkoutLoading ? "Redirecting..." : "Get Creator"}
          </button>
          {authState === "anonymous" && (
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              Already subscribed? Restore access from the{" "}
              <a href="/?restore=1" className="text-teal-600 dark:text-teal-400 hover:underline">home page</a>{" "}
              with your purchase email.
            </p>
          )}
        </div>
      )}

      {authState === "loading" && (
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-12">Checking your account&hellip;</p>
      )}
    </div>
  );
}
